import type { WebsiteRedirect } from '@/features/projects/website-redirect'

/** Only allow same-app relative redirects (prevents open redirects via ?next=). */
export function safeNext(next: string | null): string {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/'
}

const PENDING_INVITATION_KEY = 'arvaseo-pending-invitation'

export const pendingInvitation = {
  get(): string | null {
    try {
      return sessionStorage.getItem(PENDING_INVITATION_KEY)
    } catch {
      return null
    }
  },
  set(token: string) {
    try {
      sessionStorage.setItem(PENDING_INVITATION_KEY, token)
    } catch {
      /* storage unavailable: the user can reopen the email link */
    }
  },
  clear() {
    try {
      sessionStorage.removeItem(PENDING_INVITATION_KEY)
    } catch {
      /* ignore */
    }
  },
}

const PENDING_WEBSITE_KEY = 'arvaseo-pending-website'
const PENDING_REDIRECT_KEY = 'arvaseo-pending-website-redirect'

/** A website address from a link: just a host name (no path or query), or null. */
export function websiteFromLink(value: string | null): string | null {
  const host = (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/[/?#].*$/, '')
  return /^(?=.{4,253}$)[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(host) ? host : null
}

/**
 * The website a visitor brought from the free audit tool (`/register?website=…`), kept for this
 * tab: the sign-up form fills it in, and after "Sign up with Google" the Dashboard's first-website
 * field does. A website typed at sign-up whose home page sends visitors to another website is kept
 * too, with that redirect, so the Assistant's add-website step asks which one to add. Cleared once
 * a website is added.
 */
export const pendingWebsite = {
  /** The link's website (remembered), or the one remembered earlier in this tab. */
  capture(search: string): string | null {
    const fromLink = websiteFromLink(new URLSearchParams(search).get('website'))
    if (fromLink) {
      try {
        sessionStorage.setItem(PENDING_WEBSITE_KEY, fromLink)
      } catch {
        /* storage unavailable: the form still has it */
      }
      return fromLink
    }
    return pendingWebsite.get()
  },
  get(): string | null {
    try {
      return websiteFromLink(sessionStorage.getItem(PENDING_WEBSITE_KEY))
    } catch {
      return null
    }
  },
  /** Keep a website for later in this tab, and where its home page sends visitors, if it does. */
  set(website: string, redirect: WebsiteRedirect | null = null) {
    try {
      sessionStorage.setItem(PENDING_WEBSITE_KEY, website)
      if (redirect) sessionStorage.setItem(PENDING_REDIRECT_KEY, JSON.stringify(redirect))
      else sessionStorage.removeItem(PENDING_REDIRECT_KEY)
    } catch {
      /* storage unavailable: the add-website step asks again */
    }
  },
  /** Where the kept website's home page sends visitors, when sign-up found it does. */
  redirect(): WebsiteRedirect | null {
    try {
      const kept: unknown = JSON.parse(sessionStorage.getItem(PENDING_REDIRECT_KEY) ?? 'null')
      if (typeof kept !== 'object' || kept === null) return null
      const { entered, destination } = kept as Record<string, unknown>
      return typeof entered === 'string' &&
        typeof destination === 'string' &&
        entered === pendingWebsite.get() &&
        websiteFromLink(destination)
        ? { entered, destination }
        : null
    } catch {
      return null
    }
  },
  clear() {
    try {
      sessionStorage.removeItem(PENDING_WEBSITE_KEY)
      sessionStorage.removeItem(PENDING_REDIRECT_KEY)
    } catch {
      /* ignore */
    }
  },
}

/** Read `#key=value` pairs. Emailed secrets live in the fragment, never the query string. */
export function readFragment(): URLSearchParams {
  return new URLSearchParams(window.location.hash.replace(/^#/, ''))
}
